import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { Case, CaseCreate, CaseUpdate } from '../types';

export function useCases() {
  return useQuery<Case[]>({
    queryKey: ['cases'],
    queryFn: async () => {
      const { data } = await api.get('/cases');
      return data;
    },
  });
}

export function useArchivedCases() {
  return useQuery<Case[]>({
    queryKey: ['cases', 'archived'],
    queryFn: async () => {
      const { data } = await api.get('/cases/archived');
      return data;
    },
  });
}

export function useCase(publicId: string | undefined) {
  return useQuery<Case>({
    queryKey: ['cases', 'by-public-id', publicId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/by-public-id/${publicId}`);
      return data;
    },
    enabled: !!publicId,
  });
}

export function useCreateCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (caseData: CaseCreate) => {
      const { data } = await api.post('/cases', caseData);
      return data as Case;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
    },
  });
}

export function useUpdateCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: CaseUpdate }) => {
      const { data } = await api.put(`/cases/${id}`, updates);
      return data as Case;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['cases', variables.id] });
    },
  });
}

export function useArchiveCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/cases/${id}/archive`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
    },
  });
}

export function useRestoreCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/cases/${id}/restore`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
    },
  });
}

export function useDeleteCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/cases/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
    },
  });
}
